import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "src/utils";
import SubCategories from "./sub-categories";
import HandbookHeader from "./handbook-header";
import { SVGIcon, KeyboardArrowRightIcon, Box, Container, Typography, CircularProgress, Divider } from "src/components";
import { getCategoryById } from "src/data";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function HandbookSearch() {
  const query = useQuery();
  const navigate = useNavigate();
  const q = query.get("q") || "";
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!q) return;

      setLoading(true);
      setError(false);
      try {
        const res = await api.get(`/handbook/search?q=${encodeURIComponent(q)}`);
        setResults(res.data);
      } catch (err) {
        console.error("Search failed", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [q]);

  const categories = results.filter((item) => item.id === item.categoryId);
  const articles = results.filter((item) => item.id !== item.categoryId);

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        minHeight: "100%",
        background: "#FAFAFA",
        overflowY: "auto",
        width: "100%",
        pb: 8,
      }}>

        <HandbookHeader 
            title={"Search the Handbook"}
            description={error ? `Failed to load Results for: ${q}` : `Showing Results for: ${q}`}
            displayBackButton={true}
            displayErrorAlert={error}
            isLoading={loading} />
        
        <Container maxWidth="lg" sx={{ py: 3 }}>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={6}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Matching Categories */}
            {categories.length > 0 && (
              <>
                <Typography variant="h5" fontWeight={600} mb={2}>
                  Matching Categories
                </Typography>

                {categories.map((category) => {
                    const categoryIcon = getCategoryById(category.id);
                    return <Box
                        key={category.id}
                        onClick={() => handleNavigate(`/handbook/${category.slug}`)}
                        display="flex"
                        alignItems="center"
                        gap={2}
                        mb={2}
                        p={2}
                        sx={{
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        transition: "0.2s",
                        cursor: "pointer",
                        "&:hover": {
                            borderColor: "primary.main",
                            boxShadow: 2,
                        },
                        }}
                    >
                        {category.imageUrl ? (
                        <Box
                            component="img"
                            src={category.imageUrl}
                            alt={category.title}
                            sx={{
                            width: 60,
                            height: 60,
                            borderRadius: 1,
                            objectFit: "cover",
                            }}
                        />
                        ) : (
                        <SVGIcon icon={categoryIcon.icon} size={50} />
                        )}

                        <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                            {category.title}
                        </Typography>
                        {category.description && (
                            <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            >
                            {category.description}
                            </Typography>
                        )}
                        </Box>

                        <Box sx={{ flexGrow: 1 }} />
                        <KeyboardArrowRightIcon color="action" />
                    </Box>
                })}

                <Divider sx={{ my: 4 }} />
              </>
            )}

            {/* Matching Subcategories/Articles */}
            {articles.length > 0 && (
              <>
                <Typography variant="h5" fontWeight={600} mb={2}>
                  Matching Subcategories
                </Typography>

                <SubCategories
                  articles={articles}
                  categoryColor="#27789B"
                />
              </>
            )}

            {/* No results fallback */}
            {!categories.length && !articles.length && !loading && (
              <Box mt={4}>
                <Typography variant="body1" color="text.secondary">
                  No results found for <strong>"{q}"</strong>.
                </Typography>
              </Box>
            )}
          </>
        )}
        </Container>
    </Box>
  );
}
